import { initAppBrowser } from "@actions/initApp";
import ExpansionPanel from "@components/ExpansionPanel";
import styled from "@emotion/styled";
import { ClearedStyleRouterLink } from "@modules/Components";
import DefaultCustomButton from "@modules/Components/DefaultCustomButton";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { FiArrowDown } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { updateCustomerConsignmentStatus } from "../actions/consignment";
import { defaultAction } from "../actions/defaultAction";
import { SHOW_SIGN_IN_POPUP_MODAL } from "../constants/actionTypes";
import "../styles/howTo.scss";
import { Link } from "react-router-dom/cjs/react-router-dom";

const HeaderMain = styled.div`
  border-radius: 0px 0px 460px 0px;
  background-image: radial-gradient(
      circle at 100% 0,
      rgba(255, 255, 255, 0.14),
      rgba(255, 255, 255, 0) 57%
    ),
    linear-gradient(to bottom, #271763, #271763);
  position: relative;
  text-align: center;

  @media (max-width: 1275px) {
    border-radius: 0px 0px 360px 0px;
  }
  @media (max-width: 1100px) {
    border-radius: 0px 0px 200px 0px;
  }
  @media (max-width: 600px) {
    border-radius: 0px 0px 120px 0px;
  }
  @media (max-width: 400px) {
    border-radius: 0px 0px 90px 0px;
  }
`;

const Header = styled.h1`
  font-size: 72px;
  line-height: 1.25;
  text-align: center;
  color: #fff;
  max-width: 1287px;
  margin: 0 auto;
  padding-top: 72px;
  padding-bottom: 24px;

  @media (max-width: 1500px) {
    font-size: 66px;
  }
  @media (max-width: 1375px) {
    font-size: 60px;
    max-width: 985px;
  }
  @media (max-width: 1100px) {
    max-width: 95%;
    font-size: 50px;
  }
  @media (max-width: 650px) {
    font-size: 40px;
  }
  @media (max-width: 400px) {
    font-size: 30px;
  }
`;

const HeaderP = styled.div`
  opacity: 0.8;
  font-size: 18px;
  font-weight: 500;
  line-height: 2;
  text-align: center;
  color: #fff;
  max-width: 944px;
  margin: 0 auto;
  @media (max-width: 1100px) {
    max-width: 95%;
    font-size: 16px;
  }
  @media (max-width: 999px) {
    max-width: 85%;
  }
  @media (max-width: 728px) {
    max-width: 90%;
  }
  @media (max-width: 400px) {
    font-size: 14px;
  }
`;

const AbsoluteImageLeft = styled.img`
  position: absolute;
  bottom: 0;
  left: 0;

  @media (max-width: 1275px) {
    width: 250px;
    height: auto;
  }
  @media (max-width: 999px) {
    display: none;
  }
`;

const AbsoluteImageRight = styled.img`
  position: absolute;
  top: 0;
  right: 0;

  @media (max-width: 1275px) {
    width: 100px;
    height: auto;
  }
  @media (max-width: 999px) {
    display: none;
  }
`;

const CircleP = styled.div`
  width: 72px;
  height: 72px;
  box-shadow: 30px 30px 50px 0 rgba(31, 32, 33, 0.15);
  background-color: #ffffff;
  border-radius: 50%;
  display: inline-flex;
  position: relative;
  text-align: center;
  align-items: center;
  justify-content: center;
  margin-top: 36px;
  cursor: pointer;
  bottom: -33px;

  @media (max-width: 999px) {
    margin-top: 0;
    width: 54px;
    height: 54px;
    bottom: -25px;
  }
`;

const Arrow = styled(FiArrowDown)`
  color: #777984;
  font-size: 26px;
  opacity: 0.8;
`;

const TopSection = styled.section`
  padding-top: 100px;
  padding-bottom: 100px;
  @media (max-width: 767px) {
    padding-top: 60px;
    padding-bottom: 40px;
  }
`;

const BlueLine = styled.div`
  height: 3px;
  background-color: #12b2ea;
  width: 36px;
  margin: 0 auto;
`;

const ProcessDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr 50px 1fr 50px 1fr 50px 1fr;
  margin-top: 75px;
  margin-bottom: 75px;

  @media (max-width: 1150px) {
    grid-template-columns: 1fr 1fr;
    width: 80%;
    grid-row-gap: 50px;
  }
  @media (max-width: 767px) {
    width: auto;
  }
  @media (max-width: 630px) {
    grid-template-columns: 1fr;
    margin-top: 35px;
    margin-bottom: 35px;
  }
`;

const ProcessItem = styled.div`
  font-size: 18px;
  line-height: 1.67;
  text-align: center;
  color: #ffffff;
  text-align: center;
  margin: 0 auto;

  @media (max-width: 1400px) {
    font-size: 17px;
  }
  @media (max-width: 1300px) {
    font-size: 16px;
  }
  @media (max-width: 1150px) {
    font-size: 18px;
    line-height: 1.67;
    max-width: 278px;
  }
`;

const ProcessImageDiv = styled.div`
  width: 96px;
  height: 96px;
  box-shadow: 29px 29px 44px 0 rgba(31, 32, 33, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  margin: 0 auto;
`;

const ProcessText = styled.div`
  font-size: 16px;
  line-height: 1.88;
  text-align: center;
  color: #777984;
  max-width: 235px;
  margin: 0 auto;
`;

const ProcessTextHeader = styled.div`
  font-size: 18px;
  font-weight: bold;
  line-height: 1.67;
  text-align: center;
  color: #1f2021;
  padding-top: 42px;
  padding-bottom: 16px;
`;

const SquiglyImageDiv = styled.div`
  position: relative;

  @media (max-width: 1150px) {
    display: none;
  }
`;

const SquiglyImage = styled.img`
  position: absolute;
  left: -50px;
  top: 50px;
  @media (max-width: 1400px) {
    left: -37px;
    top: 38px;
    width: 129px;
  }
`;

const SquiglyImageInverted = styled.img`
  position: absolute;
  left: -50px;
  top: 11px;
  -webkit-transform: rotate(180deg);
  -ms-transform: rotate(180deg);
  transform: rotate(173deg);

  @media (max-width: 1400px) {
    left: -37px;
    width: 129px;
  }
`;

const MiddleSection = styled.section`
  box-shadow: 0 20px 80px 0 rgba(119, 121, 132, 0.1);
  background-color: #fff;
  padding-top: 50px;
  padding-bottom: 50px;
  margin-top: 100px;
  margin-bottom: 100px;

  @media (max-width: 767px) {
    padding-top: 25px;
    padding-bottom: 25px;
    margin-top: 50px;
    margin-bottom: 50px;
  }
`;

const MiddleGrid = styled.div`
  /* display: grid; */
  width: 84%;
  margin: 0 auto;
  /* gap: 44px; */
  align-items: center;
  text-align: center;
`;

const MiddleContent = styled.div`
  @media (max-width: 950px) {
    max-width: 400px;
    margin: 0 auto;
    margin-top: 30px;
  }
`;

const MiddleHeader = styled.div`
  font-size: 24px;
  line-height: 1.5;
  font-weight: 600;
  color: #1f2021;
  margin-bottom: 25px;
  text-align: center;

  @media (max-width: 1765px) {
    font-size: 22px;
  }
`;

const MiddleText = styled.div`
  font-size: 16px;
  line-height: 1.88;
  color: #777984;

  @media (max-width: 1765px) {
    font-size: 15px;
  }
`;

const Button = styled.div`
  width: 245px;
  margin: 0 auto;
  margin-top: 40px;

  @media (max-width: 1300px) {
    width: 320px;
  }
  @media (max-width: 430px) {
    width: 100%;
  }
`;

const Faqs = styled.section`
  background-image: linear-gradient(
    to bottom,
    rgba(34, 176, 252, 0.081),
    rgba(34, 176, 252, 0)
  );
  position: relative;
  min-height: 920px;

  @media (max-width: 1300px) {
    min-height: 1165px;
  }
  @media (max-width: 950px) {
    min-height: 1316px;
  }
  @media (max-width: 545px) {
    min-height: 1360px;
  }
`;

const FaqContent = styled.div`
  width: fit-content;
  margin: 0 auto;
  padding-top: 75px;
  padding-bottom: 50px;

  @media (max-width: 950px) {
    width: 85%;
  }
  @media (max-width: 520px) {
    padding-top: 50px;
  }
`;

const FaqHeader = styled.h4`
  font-size: 36px;
  text-align: center;
  color: #1f2021;
  margin-top: 25px;
  margin-bottom: 50px;
  @media (max-width: 767px) {
    margin-bottom: 36px;
    font-size: 28px;
  }
`;

const LinkSpan = styled.span`
  cursor: pointer;
  color: #ff9c1b;
`;

const howItWorksArray = [
  {
    header: "We can't accept this order right now",
    text: "While we normally buy cards upfront, we can't do that for this order right now.",
    image: "https://cdn.cardcash.com/website/ui/howiw/cart.svg"
  },
  {
    header: "Why this happens",
    text: "Sometimes our inventory or system prevents an instant purchase.",
    image: "https://cdn.cardcash.com/website/ui/howiw/card-slides-icon.svg"
  },
  {
    header: "Approve your listing",
    text: "Scroll down and tap Yes to continue. After that, we'll list your card on CardCash for shoppers to buy.",
    image: "https://cdn.cardcash.com/website/ui/howiw/support-icon-big.svg"
  },
  {
    header: "Sold & paid",
    text: "Once your card sells and 7 days pass without issues, we send your payment, same amount and same payment method you were offered.",
    image: "https://cdn.cardcash.com/website/ui/howiw/savings-icon.svg"
  }
];

const middleSection = {
  pending: {
    header: "Agreement details",
    text: (
      <MiddleText>
        <ol>
          <li>
            <b>Authorization:</b> By clicking 'I agree', you authorize CardCash
            to list your card for sale and accept the following terms.
          </li>
          <li>
            <b>Payment:</b> We'll pay you one week after your card sells,
            provided the buyer confirms there are no issues with the card. We'll
            email you when it sells.
          </li>
          <li>
            <b>Exclusivity:</b> You agree not to list this card on other
            marketplaces or use it yourself while it's listed with us.
          </li>
          <li>
            <b>Future orders:</b> This agreement covers future orders too. If we
            can't buy a card from you upfront in the future, we can list it
            under this same agreement without asking for your approval again.
          </li>
          <li>
            <b>Cancellation:</b> You can cancel this agreement at any time
            before your card sells. We'll remove digital cards from our listing
            immediately and mail back any physical cards you've sent us.
          </li>
        </ol>
      </MiddleText>
    ),
    buttonText: "I agree"
  },
  accepted: {
    header: "Your card is now listed",
    text: (
      <MiddleText>
        Your gift card is now listed on CardCash.com. We'll email you with any
        updates. You can view your card details and status on your{" "}
        <Link to="/account/listed/">account page</Link>.
        <br />
        <br />
        Changed your mind? You can cancel this agreement from your account page.
        We'll remove any unsold cards from our listings immediately. For
        physical cards you've already sent us, we'll mail them back to you.
      </MiddleText>
    ),
    buttonText: "Cancel agreement"
  },
  canceled: {
    header: "Agreement canceled",
    text: (
      <MiddleText>
        You cancelled our seller agreement and all unsold cards have been
        removed from CardCash. Any physical cards will be returned to you
        promptly.
        <br />
        <br />
        You can still sell cards to CardCash. However, if we can't pay you
        upfront for a card in the future, we'll decline the order rather than
        offering the listing option.
      </MiddleText>
    )
  },
  default: {
    header: "No active listings",
    text: (
      <MiddleText>
        You don't have any active listings yet. Ready to sell a gift card?
      </MiddleText>
    ),
    buttonText: "Login to continue"
  },
  completed: {
    header: "Your card is listed",
    text: (
      <MiddleText>
        Your card has been listed on CardCash.com and payment will be processed
        one week after it sells.
        <br />
        <br />
        Good news: You won't need to go through this approval process again. If
        we can't buy a card from you upfront in the future, we'll automatically
        list it under your existing agreement.
      </MiddleText>
    )
  },
  revoked: {
    header: "No active listings",
    text: (
      <MiddleText>
        Your seller agreement has been canceled. You don't have any active
        listings at this time.
      </MiddleText>
    )
  }
};

const faqArray = [
  {
    id: 1,
    question: "Why can’t CardCash pay me upfront this time?",
    answer:
      "Sometimes our system or inventory limits prevent us from buying certain cards instantly."
  },
  {
    id: 2,
    question: "How soon will my card be listed, and when do I get paid?",
    answer:
      "We'll list your card within 1-2 days after you approve your listing. Once it sells and 7 days pass without any issues, your payment is sent. You still receive the same amount we originally offered."
  },
  {
    id: 3,
    question:
      "Do I need to ship my card, and can I change my mind about listing?",
    answer:
      "If it's a physical card, you'll need to ship it to us before we can list it for sale. Changed your mind? No problem. Go to your account page, and you'll see a link to this page where you can cancel the agreement. For physical cards, we will mail them back to you. Once a buyer purchases your card, we can't cancel the sale."
  },
  {
    id: 4,
    question: "How's my card's price set, and what about fees or other costs?",
    answer:
      "We set the listing price for your card. You'll receive the amount we agreed upon when you submitted your card, even if it sells for more than that. There are no additional fees."
  }
];

const ConsignmentAgreement = () => {
  const dispatch = useDispatch();
  const myRef = useRef(null);
  const [oneIsOpenend, changeOneIsOpenend] = useState(false);
  const [idOfOldItem, setIdOfOldItem] = useState();
  const [otherIsClicked, changeIsOtherClicked] = useState("");
  const signedIn = useSelector(state => state.initAppReducer.signedIn);
  const currentConsignmentStatus = useSelector(
    state => state.customerInfo.riskConsignmentProposalStatus
  ); // null, "pending", "accepted", "revoked", "completed" - by CardCash, "canceled" - by customer
  const isCustomerLoading = useSelector(
    state => state.customerInfo._customerInfoThunkRunning
  );

  useEffect(() => {
    dispatch(initAppBrowser(["messages"]));
    window.scrollTo(0, 0);
  }, []);

  const handleCircleClick = () => {
    executeScroll();
  };

  const executeScroll = () => scrollToRef(myRef);

  const scrollToRef = ref => window.scrollTo(0, ref.current.offsetTop);

  const handleOtherClicked = id => {
    if (oneIsOpenend && idOfOldItem === id) {
      changeOneIsOpenend(false);
      setIdOfOldItem();
    } else {
      changeOneIsOpenend(true);
    }
    changeIsOtherClicked(id);
  };

  return (
    <div className="mar-t-128">
      <div>
        <Helmet
          title={"How to save with gift cards"}
          meta={[
            {
              name: "description",
              content:
                "Learn how to save safely with discounted gift cards from CardCash, the world's largest gift card exchange."
            }
          ]}
        />
        <HeaderMain>
          <AbsoluteImageLeft
            src="https://cdn.cardcash.com/home/images/other/element-background-group.svg"
            alt=""
          />
          <AbsoluteImageRight
            src="https://cdn.cardcash.com/website/ui/howiw/top-right-elements.svg"
            alt=""
          />

          <Header>We can't pay upfront for this order</Header>
          <HeaderP>
            We usually pay upfront, but we can't do that for this order. You can
            still get paid, just through a different process. Here's how it
            works:
            <br />
            <br />
          </HeaderP>
          <CircleP onClick={handleCircleClick}>
            <Arrow className="arrow" />
          </CircleP>
        </HeaderMain>

        <main>
          <TopSection>
            <ProcessDiv ref={myRef} className="main-container-page-width">
              {howItWorksArray.map((item, index) => (
                <React.Fragment key={index}>
                  <ProcessItem>
                    <ProcessImageDiv className="process-image-div">
                      <img src={item.image} alt="" />
                    </ProcessImageDiv>
                    <ProcessTextHeader>{item.header}</ProcessTextHeader>
                    <ProcessText>
                      {item.text}
                      {item.link && (
                        <span>
                          <LinkSpan>
                            <ClearedStyleRouterLink to={item.link}>
                              &nbsp;{item.linkText}
                            </ClearedStyleRouterLink>
                          </LinkSpan>
                          {item.afterLinkText}
                        </span>
                      )}
                    </ProcessText>
                  </ProcessItem>
                  {index === 0 || index === 2 ? (
                    <SquiglyImageDiv>
                      <SquiglyImage
                        src="https://cdn.cardcash.com/website/ui/howiw/DottedLines.svg"
                        alt=""
                      />
                    </SquiglyImageDiv>
                  ) : index === 1 ? (
                    <SquiglyImageDiv>
                      <SquiglyImageInverted
                        src="https://cdn.cardcash.com/website/ui/howiw/DottedLines.svg"
                        alt=""
                      />
                    </SquiglyImageDiv>
                  ) : null}
                </React.Fragment>
              ))}
            </ProcessDiv>

            <BlueLine />
            <MiddleSection className="main-container-page-width">
              <MiddleGrid>
                <MiddleContent>
                  <MiddleHeader>
                    {
                      middleSection[currentConsignmentStatus || "default"]
                        .header
                    }
                  </MiddleHeader>
                  {middleSection[currentConsignmentStatus || "default"].text}
                </MiddleContent>
                {(!signedIn ||
                  (!["revoked", "canceled", "accepted", "completed"].includes(
                    currentConsignmentStatus
                  ) &&
                    currentConsignmentStatus)) && (
                  <Button>
                    <DefaultCustomButton
                      handleClick={() => {
                        if (!signedIn) {
                          return dispatch(
                            defaultAction(
                              SHOW_SIGN_IN_POPUP_MODAL,
                              "consignmentAgreementPage"
                            )
                          );
                        } else {
                          return dispatch(
                            updateCustomerConsignmentStatus(
                              currentConsignmentStatus === "accepted"
                                ? "cancel"
                                : "accept"
                            )
                          );
                        }
                      }}
                      classes={"main-default-orange-button"}
                      type="button"
                      disabled={isCustomerLoading}
                      buttonType="ORANGE_BUTTON"
                    >
                      {isCustomerLoading
                        ? "Loading..."
                        : middleSection[currentConsignmentStatus || "default"]
                            .buttonText}
                    </DefaultCustomButton>
                  </Button>
                )}
              </MiddleGrid>
            </MiddleSection>
          </TopSection>

          <Faqs>
            <FaqContent>
              <BlueLine />
              <FaqHeader>Some more details</FaqHeader>
              {faqArray.map(item => (
                <ExpansionPanel
                  header={"Frequently asked questions"}
                  key={item.id}
                  item={item}
                  handleOtherClicked={handleOtherClicked}
                  otherIsClicked={otherIsClicked}
                  currentHeader={"Frequently asked questions"}
                  oneIsOpenend={oneIsOpenend}
                  idOfOldItem={idOfOldItem}
                  setIdOfOldItem={setIdOfOldItem}
                />
              ))}
            </FaqContent>
          </Faqs>
        </main>
      </div>
    </div>
  );
};

export default ConsignmentAgreement;
